import { useDateNow, useReduxState } from 'hooks';
import { addMonths } from 'date-fns';
import { getMessageWithPlaceholders } from 'shared-locales';
import { getFormattedDate } from 'lib/common';
import { Badge, FlexContainer, ProgressBar } from 'components/common';

import type { Achievement, AchievementData } from 'shared-types';

interface Props {
  achievementData: Pick<
    AchievementData,
    | 'completionDate'
    | 'expirationDate'
    | 'isConfidential'
    | 'isInactive'
    | 'numberOfCompletedTemplateItems'
    | 'status'
  >;
  achievementType: Achievement['type'];
  numberOfCompletions?: number;
  numberOfItems: number;
  showCompletedTextLabel?: boolean;
  showPercentage?: boolean;
}

const AchievementUserStatus = ({
  achievementData,
  achievementType,
  numberOfCompletions,
  numberOfItems,
  showCompletedTextLabel = false,
  showPercentage = false
}: Props) => {
  const { dateNow } = useDateNow();

  const { messages } = useReduxState();

  const { completionDate, expirationDate, numberOfCompletedTemplateItems } =
    achievementData || {};

  const expired =
    expirationDate && Date.parse(expirationDate as string) < dateNow;

  const expiring =
    !expired &&
    expirationDate &&
    new Date(Date.parse(expirationDate as string)) < addMonths(dateNow, 3);

  const progress = numberOfItems
    ? numberOfCompletedTemplateItems / numberOfItems
    : 0;

  const progressText = `${numberOfCompletedTemplateItems}/${numberOfItems}`;

  return numberOfCompletions > 1 ? (
    <Badge
      isRounded={false}
      text={getMessageWithPlaceholders({
        message: messages.achievementNumberOfAchievementCompletions,
        placeholders: { numberOfCompletions }
      })}
    />
  ) : !achievementData ? (
    <Badge isRounded={false} text={messages.commonNotAdded} />
  ) : achievementData.isInactive ? (
    <Badge icon="archive" isRounded={false} text={messages.commonArchived} />
  ) : (
    <FlexContainer style={{ gap: 5 }}>
      {achievementData.isInactive ? (
        <Badge
          icon="archive"
          isRounded={false}
          text={messages.commonArchived}
        />
      ) : achievementData.status === 'not started' ? (
        <Badge
          isRounded={false}
          text={
            ['conversation', 'tasklist'].includes(achievementType)
              ? messages.commonNotStarted
              : messages.commonNoCompletion
          }
        />
      ) : achievementData.status === 'completed' ? (
        <Badge
          category={
            expired
              ? 'error-light'
              : expiring
              ? 'warning-light'
              : 'success-light'
          }
          icon="check"
          isRounded={false}
          text={
            showCompletedTextLabel
              ? expired
                ? messages.commonExpired
                : expiring
                ? messages.commonExpiring
                : messages.commonCompleted
              : getFormattedDate({ date: completionDate }) ||
                messages.commonCompleted
          }
          {...(expirationDate
            ? {
                tooltip: `${
                  expired ? messages.commonExpired : messages.commonExpires
                } ${getFormattedDate({ date: expirationDate })}`
              }
            : {})}
        />
      ) : (
        <Badge
          isRounded={false}
          text={
            <div style={{ display: 'inline-flex' }}>
              <div>
                {showPercentage
                  ? `${Math.round(progress * 100)}%`
                  : progressText}
              </div>
              <ProgressBar
                color="#555"
                progress={progress}
                style={{ margin: '6px 0 5px 8px', width: 60 }}
              />
            </div>
          }
        />
      )}

      {achievementData.isConfidential ? (
        <Badge
          category="error-light"
          icon="lock"
          isRounded={false}
          style={{ height: 24, width: 24, padding: '3px 7px 4px' }}
          tooltip={messages.commonConfidential}
        />
      ) : null}
    </FlexContainer>
  );
};

export default AchievementUserStatus;
